// The reader's own zone (FRO-147), read from the meta tag the layout prints. Every script that
// formats a time in the browser reads it from here rather than from a value on its own element:
// a card broadcast to a whole thread is rendered once, with nobody's session, so only the page it
// lands in knows whose zone it should be read in.
//
// Read on every call rather than cached: Settings replaces the tag in place when the zone is saved,
// and the time popover is turbo-permanent, so a cached value would outlive the change.
const META_SELECTOR = 'meta[name="time-zone"]'

export function viewerTimeZone() {
  return document.querySelector(META_SELECTOR)?.content || "UTC"
}

// "Eastern Time (US & Canada)" — the name Settings shows, so the popover and the picker agree.
export function viewerTimeZoneLabel() {
  return document.querySelector(META_SELECTOR)?.dataset.label || viewerTimeZone()
}

// ApplicationHelper::ABSOLUTE_TIMESTAMP_FORMAT ("%b %-d, %Y · %-l:%M %p") in the browser. Built
// from parts so the result is the server's string to the character — Intl's own joiners include a
// narrow no-break space before AM/PM that Ruby never prints.
export function formatAbsoluteTimestamp(instant, timeZone) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    }).formatToParts(instant).map((part) => [ part.type, part.value ])
  )

  return `${parts.month} ${parts.day}, ${parts.year} · ${parts.hour}:${parts.minute} ${parts.dayPeriod.toUpperCase()}`
}

// Re-prints the absolute stamps in a broadcast card on the reader's zone before Turbo inserts it.
// The server marks each one with the zone it was printed in (timestamp_tag absolute: true, and the
// comment's Edited tooltip), so a stamp already on the reader's zone — every one a request of
// their own rendered — is left exactly as it came.
//
// Replaces only the formatted substring: if the server's wording around it ever changes, or the
// two formatters ever disagree, the stamp is left alone rather than mangled.
export function localizeTimes(root, timeZone = viewerTimeZone()) {
  root.querySelectorAll("time[data-time-zone][datetime]").forEach((element) => {
    relabel(element, element.getAttribute("datetime"), timeZone, (from, to) => {
      element.textContent = element.textContent.replace(from, to)
    })
  })

  root.querySelectorAll("[data-time-zone][data-title-time]").forEach((element) => {
    relabel(element, element.dataset.titleTime, timeZone, (from, to) => {
      element.title = element.title.replace(from, to)
    })
  })
}

function relabel(element, stamp, timeZone, rewrite) {
  const printedIn = element.dataset.timeZone
  if (!stamp || printedIn === timeZone) return

  const instant = new Date(stamp)
  if (Number.isNaN(instant.getTime())) return

  try {
    rewrite(formatAbsoluteTimestamp(instant, printedIn), formatAbsoluteTimestamp(instant, timeZone))
    element.dataset.timeZone = timeZone
  } catch {
    // An identifier this browser's Intl does not know: keep the server's text, which is still true
    // on the zone it names in the popover.
  }
}
